// Where visitors come from, for marketing decisions (admin Engagement page).
//
// On the first visit the app remembers the campaign tags in the link (utm_*),
// the site that sent the visitor (host name only, never the full address) and
// the page they landed on (path only, no query string). That "first touch" is
// sent with sign-up, so each account knows which ad, post, QR code or search
// brought it in. Each browser session also records one `site_visit` event so
// visits can be compared with sign-ups for each source.
//
// Nothing here is shared with other companies: it is stored in TradePrep's own
// database (see the Privacy page).

import { siteFromHost } from '@core/site.ts';
import { getAnonymousId, storage } from '@/lib/platform/storage';
import { getSite } from '@/lib/site';
import { track } from '@/features/analytics/track';

export const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;

export interface Touch {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  referrer_host?: string;
  landing_path: string;
  first_seen_at: string;
}

/** Answers to "How did you hear about TradePrep?" (the database keeps the same list). */
export const HEARD_ABOUT_OPTIONS = [
  { value: 'training_provider', label: 'My training provider or tutor' },
  { value: 'employer', label: 'My employer or workmates' },
  { value: 'friend', label: 'A friend or family member' },
  { value: 'search', label: 'Google or another search' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'other', label: 'Somewhere else' },
] as const;

export type HeardAbout = (typeof HEARD_ABOUT_OPTIONS)[number]['value'];

export const heardAboutLabel = (value: string) =>
  value === 'not_answered' ? 'Didn’t answer' : (HEARD_ABOUT_OPTIONS.find((o) => o.value === value)?.label ?? value);

const FIRST_TOUCH_KEY = 'tradeprep.first_touch';
const VISIT_KEY = 'tradeprep.visit_recorded';
/** A first touch older than this is replaced by the next visit. */
const FIRST_TOUCH_DAYS = 90;

// Hosts that are part of the journey, not where someone came from.
const IGNORED_REFERRERS = /(^|\.)(stripe\.com|supabase\.co|accounts\.google\.com)$/;

function clean(value: string | null | undefined, max: number): string | undefined {
  const v = (value ?? '').replace(/[\u0000-\u001f]/g, '').trim();
  return v ? v.slice(0, max) : undefined;
}

/** The host of another site that linked here, or undefined (no referrer, our own sites, payment pages). */
export function referrerHost(referrer: string, currentHost: string): string | undefined {
  if (!referrer) return undefined;
  let host: string;
  try {
    host = new URL(referrer).hostname.toLowerCase();
  } catch {
    return undefined;
  }
  if (!host || host === currentHost.toLowerCase() || siteFromHost(host) || IGNORED_REFERRERS.test(host)) return undefined;
  return host.slice(0, 200);
}

/** What this visit says about where the visitor came from. */
export function readTouch(location: { search: string; pathname: string; hostname: string }, referrer: string, now = new Date()): Touch {
  const params = new URLSearchParams(location.search);
  const touch: Touch = { landing_path: location.pathname.slice(0, 300) || '/', first_seen_at: now.toISOString() };
  for (const key of UTM_KEYS) {
    const value = clean(params.get(key), 100);
    if (value) touch[key] = value;
  }
  const host = referrerHost(referrer, location.hostname);
  if (host) touch.referrer_host = host;
  return touch;
}

function parseTouch(raw: string | null): Touch | null {
  if (!raw) return null;
  try {
    const t = JSON.parse(raw) as Touch;
    return t && typeof t.landing_path === 'string' && typeof t.first_seen_at === 'string' ? t : null;
  } catch {
    return null;
  }
}

export async function getFirstTouch(): Promise<Touch | null> {
  return parseTouch(await storage.getItem(FIRST_TOUCH_KEY));
}

function sessionFlag(): { seen: boolean; mark: () => void } {
  try {
    return { seen: window.sessionStorage.getItem(VISIT_KEY) === '1', mark: () => window.sessionStorage.setItem(VISIT_KEY, '1') };
  } catch {
    return { seen: false, mark: () => undefined };
  }
}

let captured = false;

/**
 * Run once when the app starts: remember the first touch (if there isn't a
 * recent one) and record one site visit per browser session.
 */
export async function captureVisit(win: Window = window): Promise<void> {
  if (captured) return;
  captured = true;
  try {
    const touch = readTouch(win.location, win.document.referrer);
    const first = await getFirstTouch();
    const stale = first && Date.now() - new Date(first.first_seen_at).getTime() > FIRST_TOUCH_DAYS * 86_400_000;
    if (!first || stale) await storage.setItem(FIRST_TOUCH_KEY, JSON.stringify(touch));

    const flag = sessionFlag();
    if (!flag.seen) {
      flag.mark();
      const { first_seen_at: _ignored, ...properties } = touch;
      track('site_visit', { properties: { ...properties, site: getSite().code } });
    }
  } catch {
    /* analytics must never break the app */
  }
}

/** For tests. */
export function resetCaptureForTests() {
  captured = false;
}

/** Sent with sign-up (the database cleans and caps every field). */
export async function signupAcquisition(heardAbout?: HeardAbout | '' | null, heardAboutOther?: string): Promise<Record<string, string>> {
  const out: Record<string, string> = {};
  try {
    const first = await getFirstTouch();
    if (first) for (const [k, v] of Object.entries(first)) if (typeof v === 'string' && v) out[k] = v;
    out.anonymous_id = await getAnonymousId();
  } catch {
    /* ignore */
  }
  if (!out.landing_path) out.landing_path = '/register';
  if (heardAbout) out.heard_about = heardAbout;
  const other = clean(heardAboutOther, 120);
  if (heardAbout === 'other' && other) out.heard_about_other = other;
  return out;
}
